import { useEffect, useMemo, useState } from 'react';
import { getRandomLoadingPhrases, LoadingPhrase } from '../redux/api/loadingPhrases';

const DEFAULT_LIMIT = 10;
const DEFAULT_ROTATION_MS = 4000;
// Espelha o TTL de cache do backend (300s) para evitar refetch desnecessário.
const CACHE_TTL_MS = 5 * 60 * 1000;

interface UseLoadingPhrasesOptions {
  courseTag?: string | null;
  enabled?: boolean;
  limit?: number;
  rotationInterval?: number;
}

interface UseLoadingPhrasesReturn {
  phrase: string | null;
  /** Índice atual — útil como `key` para reiniciar a animação de transição. */
  index: number;
  hasPhrases: boolean;
}

interface CacheEntry {
  phrases: LoadingPhrase[];
  timestamp: number;
}

// Cache em memória (por sessão) para não impactar performance com chamadas repetidas.
const phrasesCache = new Map<string, CacheEntry>();
// Deduplicação de requisições em voo para a mesma chave.
const inFlight = new Map<string, Promise<LoadingPhrase[]>>();

const buildCacheKey = (courseTag: string | null | undefined, limit: number) =>
  `${courseTag || 'global'}:${limit}`;

const shuffle = <T,>(items: T[]): T[] => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

async function fetchPhrases(
  courseTag: string | null | undefined,
  limit: number
): Promise<LoadingPhrase[]> {
  const cacheKey = buildCacheKey(courseTag, limit);
  const cached = phrasesCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.phrases;
  }

  if (inFlight.has(cacheKey)) {
    return inFlight.get(cacheKey) as Promise<LoadingPhrase[]>;
  }

  const promise = getRandomLoadingPhrases({ courseTag, limit })
    .then((phrases) => {
      phrasesCache.set(cacheKey, { phrases, timestamp: Date.now() });
      return phrases;
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, promise);
  return promise;
}

/**
 * Pré-carrega as frases globais e as guarda no cache em memória (variável global da sessão).
 *
 * Deve ser chamado uma vez ao entrar no sistema (usuário autenticado), de forma que
 * qualquer tela que exiba o loading já encontre as frases prontas, sem novo request.
 */
export async function prefetchLoadingPhrases(limit: number = DEFAULT_LIMIT): Promise<void> {
  try {
    await fetchPhrases(null, limit);
  } catch {
    // Silencioso: se falhar, as telas apenas exibem o texto padrão de loading.
  }
}

/**
 * Hook que busca frases educativas e as alterna automaticamente durante o loading.
 *
 * - Busca um lote de frases uma única vez (cacheado em memória).
 * - Embaralha localmente para exibir em ordem aleatória.
 * - Alterna as frases com fade suave, sem flickering.
 * - Respeita frases do curso (via `courseTag`) com fallback global feito pelo backend.
 */
export function useLoadingPhrases({
  courseTag,
  enabled = true,
  limit = DEFAULT_LIMIT,
  rotationInterval = DEFAULT_ROTATION_MS,
}: UseLoadingPhrasesOptions = {}): UseLoadingPhrasesReturn {
  const [phrases, setPhrases] = useState<string[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let isMounted = true;

    fetchPhrases(courseTag, limit)
      .then((result) => {
        if (!isMounted) return;
        const texts = result.map((item) => item.text).filter(Boolean);
        setPhrases(shuffle(texts));
        setIndex(0);
      })
      .catch(() => {
        // Silencioso: em caso de falha, o componente exibe o texto padrão de loading.
        if (isMounted) setPhrases([]);
      });

    return () => {
      isMounted = false;
    };
  }, [courseTag, enabled, limit]);

  useEffect(() => {
    if (!enabled || phrases.length <= 1) return undefined;

    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % phrases.length);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [enabled, phrases.length, rotationInterval]);

  return useMemo(
    () => ({
      phrase: phrases.length > 0 ? phrases[index] : null,
      index,
      hasPhrases: phrases.length > 0,
    }),
    [phrases, index]
  );
}

export default useLoadingPhrases;
